import { 
  collection, 
  query, 
  orderBy, 
  getDocs, 
  doc, 
  updateDoc, 
  deleteDoc,
  onSnapshot,
  where,
  limit,
  startAfter,
  Timestamp 
} from 'firebase/firestore';
import { db } from './firebaseConfig';

/**
 * Quotes Service - Handle all Firestore operations for quotes
 */

// Collection reference
const QUOTES_COLLECTION = 'quotes';

/**
 * Helper function to safely convert date to Firestore Timestamp
 * @param {Date} date - Date to convert
 * @param {string} type - Type for logging (start/end)
 * @returns {Timestamp|null} Firestore Timestamp or null if invalid
 */
const createTimestampFilter = (date, type) => {
  if (!date || !(date instanceof Date) || isNaN(date)) {
    console.warn(`Invalid ${type} date:`, date);
    return null;
  }
  
  try {
    return Timestamp.fromDate(date);
  } catch (error) {
    console.error(`Error converting ${type} date to timestamp:`, error);
    return null;
  }
};

/**
 * Helper function to normalize date to start of day
 * @param {Date} date - Date to normalize
 * @returns {Date} Date set to start of day (00:00:00.000)
 */
const startOfDay = (date) => {
  const newDate = new Date(date);
  newDate.setHours(0, 0, 0, 0);
  return newDate;
};

/**
 * Helper function to normalize date to end of day
 * @param {Date} date - Date to normalize
 * @returns {Date} Date set to end of day (23:59:59.999)
 */
const endOfDay = (date) => {
  const newDate = new Date(date);
  newDate.setHours(23, 59, 59, 999);
  return newDate;
};

/**
 * Get all quotes with real-time updates
 * @param {Function} callback - Function to call when data updates
 * @param {Object} options - Query options (status, limit, orderBy, startDate, endDate)
 * @returns {Function} Unsubscribe function
 */
export const subscribeToQuotes = (callback, options = {}) => {
  try {
    let q = collection(db, QUOTES_COLLECTION);
    
    // Add filters
    if (options.status) {
      q = query(q, where('status', '==', options.status));
    }
    
    // Add date range filters with proper validation and boundaries
    if (options.startDate) {
      const normalizedStartDate = startOfDay(options.startDate);
      const startTimestamp = createTimestampFilter(normalizedStartDate, 'start');
      if (startTimestamp) {
        q = query(q, where('createdAt', '>=', startTimestamp));
      }
    }
    
    if (options.endDate) {
      const normalizedEndDate = endOfDay(options.endDate);
      const endTimestamp = createTimestampFilter(normalizedEndDate, 'end');
      if (endTimestamp) {
        q = query(q, where('createdAt', '<=', endTimestamp));
      }
    }
    
    // Debug logging for date filtering
    if (options.startDate || options.endDate) {
      console.log('Date filtering applied:', {
        startDate: options.startDate,
        endDate: options.endDate,
        normalizedStart: options.startDate ? startOfDay(options.startDate) : null,
        normalizedEnd: options.endDate ? endOfDay(options.endDate) : null
      });
    }
    
    // Add ordering
    const orderField = options.orderBy || 'createdAt';
    const orderDirection = options.orderDirection || 'desc';
    q = query(q, orderBy(orderField, orderDirection));
    
    // Add limit
    if (options.limit) {
      q = query(q, limit(options.limit));
    }

    console.log('📊 QuotesService: Setting up subscription with options:', {
      hasStartDate: !!options.startDate,
      hasEndDate: !!options.endDate,
      isAllTime: !options.startDate && !options.endDate,
      orderBy: orderField
    });

    return onSnapshot(q, (snapshot) => {
      const quotes = [];
      snapshot.forEach((doc) => {
        const data = doc.data();
        quotes.push({
          id: doc.id,
          ...data,
          // Convert Firestore timestamp to readable format
          createdAt: data.createdAt?.toDate(),
        });
      });
      
      console.log(`✅ QuotesService: Received ${quotes.length} quote requests`);
      
      callback(quotes);
    }, (error) => {
      console.error('Error fetching quotes:', error);
      callback([]);
    });
  } catch (error) {
    console.error('Error setting up quotes subscription:', error);
    callback([]);
    return () => {}; // Return empty unsubscribe function
  }
};

/**
 * Get quotes once (without real-time updates)
 * @param {Object} options - Query options (status, startDate, endDate, limit, orderBy)
 * @returns {Promise<Array>} Array of quotes
 */
export const getQuotes = async (options = {}) => {
  try {
    let q = collection(db, QUOTES_COLLECTION);
    
    if (options.status) {
      q = query(q, where('status', '==', options.status));
    }
    
    // Add date range filters with proper validation and boundaries
    if (options.startDate) {
      const normalizedStartDate = startOfDay(options.startDate);
      const startTimestamp = createTimestampFilter(normalizedStartDate, 'start');
      if (startTimestamp) {
        q = query(q, where('createdAt', '>=', startTimestamp));
      }
    }
    
    if (options.endDate) {
      const normalizedEndDate = endOfDay(options.endDate);
      const endTimestamp = createTimestampFilter(normalizedEndDate, 'end');
      if (endTimestamp) {
        q = query(q, where('createdAt', '<=', endTimestamp));
      }
    }
    
    const orderField = options.orderBy || 'createdAt';
    const orderDirection = options.orderDirection || 'desc';
    q = query(q, orderBy(orderField, orderDirection));
    
    if (options.limit) {
      q = query(q, limit(options.limit));
    }

    const snapshot = await getDocs(q);
    const quotes = [];
    
    snapshot.forEach((doc) => {
      const data = doc.data();
      quotes.push({
        id: doc.id,
        ...data,
        createdAt: data.createdAt?.toDate(),
      });
    });
    
    return quotes;
  } catch (error) {
    console.error('Error getting quotes:', error);
    return [];
  }
};

/**
 * Update quote status
 * @param {string} quoteId - Quote document ID
 * @param {string} newStatus - New status ('new', 'contacted', 'quoted', 'closed')
 * @returns {Promise<boolean>} Success status
 */
export const updateQuoteStatus = async (quoteId, newStatus) => {
  try {
    const quoteRef = doc(db, QUOTES_COLLECTION, quoteId);
    await updateDoc(quoteRef, {
      status: newStatus,
      updatedAt: new Date()
    });
    console.log(`Quote ${quoteId} status updated to: ${newStatus}`);
    return true;
  } catch (error) {
    console.error('Error updating quote status:', error);
    return false;
  }
};

/**
 * Add notes to a quote
 * @param {string} quoteId - Quote document ID  
 * @param {string} notes - Admin notes
 * @returns {Promise<boolean>} Success status
 */
export const addQuoteNotes = async (quoteId, notes) => {
  try {
    const quoteRef = doc(db, QUOTES_COLLECTION, quoteId);
    await updateDoc(quoteRef, {
      adminNotes: notes,
      updatedAt: new Date()
    });
    console.log(`Notes added to quote ${quoteId}`);
    return true;
  } catch (error) {
    console.error('Error adding quote notes:', error);
    return false;
  }
};

/**
 * Update quote with quotation information
 * @param {string} quoteId - Quote document ID
 * @param {string} quotationNumber - Quotation number
 * @param {string} quotationId - Quotation document ID
 * @returns {Promise<boolean>} Success status
 */
export const updateQuoteWithQuotation = async (quoteId, quotationNumber, quotationId) => {
  try {
    const quoteRef = doc(db, QUOTES_COLLECTION, quoteId);
    await updateDoc(quoteRef, {
      status: QUOTE_STATUSES.QUOTED,
      quotationNumber: quotationNumber,
      quotationId: quotationId,
      quotedAt: new Date(),
      updatedAt: new Date()
    });
    console.log(`Quote ${quoteId} updated with quotation ${quotationNumber}`);
    return true;
  } catch (error) {
    console.error('Error updating quote with quotation:', error);
    return false;
  }
};

/**
 * Delete a quote
 * @param {string} quoteId - Quote document ID
 * @returns {Promise<boolean>} Success status
 */
export const deleteQuote = async (quoteId) => {
  try {
    await deleteDoc(doc(db, QUOTES_COLLECTION, quoteId));
    console.log(`Quote ${quoteId} deleted`);
    return true;
  } catch (error) {
    console.error('Error deleting quote:', error);
    return false;
  }
};

/**
 * Get quotes statistics
 * @param {Object} options - Options for date range filtering
 * @returns {Promise<Object>} Statistics object
 */
export const getQuoteStats = async (options = {}) => {
  try {
    const allQuotes = await getQuotes(options);
    
    const stats = {
      total: allQuotes.length,
      new: allQuotes.filter(q => q.status === 'new').length,
      contacted: allQuotes.filter(q => q.status === 'contacted').length,
      quoted: allQuotes.filter(q => q.status === 'quoted').length,
      closed: allQuotes.filter(q => q.status === 'closed').length,
      thisMonth: 0,
      thisWeek: 0
    };

    // Calculate this month and week stats only if no date range is specified
    if (!options.startDate && !options.endDate) {
      const now = new Date();
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      const startOfWeek = new Date(now.setDate(now.getDate() - now.getDay()));
      
      stats.thisMonth = allQuotes.filter(q => 
        q.createdAt && q.createdAt >= startOfMonth
      ).length;
      
      stats.thisWeek = allQuotes.filter(q => 
        q.createdAt && q.createdAt >= startOfWeek
      ).length;
    }

    return stats;
  } catch (error) {
    console.error('Error getting quote statistics:', error);
    return {
      total: 0,
      new: 0,
      contacted: 0,
      quoted: 0,
      closed: 0,
      thisMonth: 0,
      thisWeek: 0
    };
  }
};

/**
 * Search quotes by customer name or email
 * @param {string} searchTerm - Search term
 * @returns {Promise<Array>} Filtered quotes
 */
export const searchQuotes = async (searchTerm) => {
  try {
    const quotes = await getQuotes();
    const searchLower = searchTerm.toLowerCase();
    
    return quotes.filter(quote => 
      quote.customerName.toLowerCase().includes(searchLower) ||
      quote.customerEmail.toLowerCase().includes(searchLower) ||
      quote.companyName.toLowerCase().includes(searchLower)
    );
  } catch (error) {
    console.error('Error searching quotes:', error);
    return [];
  }
};

// Export status constants
export const QUOTE_STATUSES = {
  NEW: 'new',
  CONTACTED: 'contacted', 
  QUOTED: 'quoted',
  CLOSED: 'closed'
};

export const STATUS_LABELS = {
  [QUOTE_STATUSES.NEW]: 'New',
  [QUOTE_STATUSES.CONTACTED]: 'Contacted',
  [QUOTE_STATUSES.QUOTED]: 'Quoted',
  [QUOTE_STATUSES.CLOSED]: 'Closed'
};

export const STATUS_COLORS = {
  [QUOTE_STATUSES.NEW]: '#ff4d4f',      // Red
  [QUOTE_STATUSES.CONTACTED]: '#1890ff', // Blue  
  [QUOTE_STATUSES.QUOTED]: '#faad14',    // Orange
  [QUOTE_STATUSES.CLOSED]: '#52c41a'     // Green
};
